/**
 * JobDetail — detailed breakdown of a single verification job.
 * Route: /history/:jobId
 *
 * Shows:
 *  - Job header (filename, status, timing)
 *  - Global stacked category bar (all sheets combined)
 *  - Per-sheet accordion → per-column cards with stacked bar + category chips
 */

import React, { useEffect, useState } from 'react';
import WritebackBadge from '../components/WritebackBadge';
import { RerunFailedAnalysisButton } from '../components/RerunFailedAnalysisButton';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { statusApi, downloadApi } from '../services/api';
import { CATEGORY_COLORS, CATEGORY_LABELS, PhoneCategory } from '../types';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ColumnBreakdown {
  column_name: string;
  total: number;
  categories: Record<string, number>;
}

interface SheetBreakdown {
  sheet_name: string;
  total: number;
  columns: ColumnBreakdown[];
}

interface JobBreakdown {
  job_id: string;
  filename: string;
  status: string;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  total_numbers: number;
  processed_count: number;
  export_ready: boolean;
  selected_sheets: string[];
  column_map: Record<string, string[]>;
  sheets: SheetBreakdown[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_STYLE: Record<string, { bg: string; color: string }> = {
  completed: { bg: 'var(--status-job-completed-bg)', color: 'var(--status-job-completed-fg)' },
  failed:    { bg: 'var(--status-job-failed-bg)', color: 'var(--status-job-failed-fg)' },
  cancelled: { bg: 'var(--status-job-cancelled-bg)', color: 'var(--status-job-cancelled-fg)' },
  processing:{ bg: 'var(--status-job-processing-bg)', color: 'var(--status-job-processing-fg)' },
  pending:   { bg: 'var(--status-job-pending-bg)', color: 'var(--status-job-pending-fg)' },
};

function fmtTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function elapsed(start: string | null, end: string | null): string {
  if (!start || !end) return '—';
  const s = Math.floor((new Date(end).getTime() - new Date(start).getTime()) / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem ? `${m}m ${rem}s` : `${m}m`;
}

// ── MiniBar — stacked proportion bar ─────────────────────────────────────────

interface MiniBarProps {
  categories: Record<string, number>;
  total: number;
}

const MiniBar: React.FC<MiniBarProps> = ({ categories, total }) => {
  if (total === 0) return null;
  const sorted = Object.entries(categories).sort(([, a], [, b]) => b - a);
  return (
    <div style={{
      display: 'flex', height: 14, borderRadius: 7,
      overflow: 'hidden', width: '100%', background: 'var(--status-neutral-bg)',
    }}>
      {sorted.map(([cat, count]) => (
        <div
          key={cat}
          title={`${CATEGORY_LABELS[cat as PhoneCategory] || cat}: ${count} (${((count / total) * 100).toFixed(1)}%)`}
          style={{
            width: `${(count / total) * 100}%`,
            background: CATEGORY_COLORS[cat as PhoneCategory] || 'var(--status-neutral-bg)',
            transition: 'width 0.3s',
          }}
        />
      ))}
    </div>
  );
};

// ── CategoryChips ─────────────────────────────────────────────────────────────

interface ChipsProps {
  categories: Record<string, number>;
  total: number;
}

const CategoryChips: React.FC<ChipsProps> = ({ categories, total }) => {
  const sorted = Object.entries(categories).sort(([, a], [, b]) => b - a);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
      {sorted.map(([cat, count]) => (
        <span
          key={cat}
          style={{
            padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600,
            background: CATEGORY_COLORS[cat as PhoneCategory] || 'var(--status-neutral-bg)',
            color: 'var(--text-strong)',
          }}
        >
          {CATEGORY_LABELS[cat as PhoneCategory] || cat}: {count}
          <span style={{ fontWeight: 400, color: 'var(--text-soft)', marginLeft: 3 }}>
            ({total > 0 ? ((count / total) * 100).toFixed(1) : '0.0'}%)
          </span>
        </span>
      ))}
    </div>
  );
};

// ── ColumnCard ────────────────────────────────────────────────────────────────

const ColumnCard: React.FC<{ col: ColumnBreakdown }> = ({ col }) => {
  const goodCount = col.categories['GOOD'] || 0;
  const goodRate = col.total > 0 ? ((goodCount / col.total) * 100).toFixed(1) : '0.0';
  const topCat = Object.entries(col.categories).sort(([, a], [, b]) => b - a)[0];

  return (
    <div style={{
      border: '1px solid var(--border)', borderRadius: 10,
      background: 'var(--surface-muted)', padding: '14px 18px', marginBottom: 10,
    }}>
      {/* Column header row */}
      <div style={{
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14 }}>📞</span>
          <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-strong)' }}>
            {col.column_name || '(unnamed)'}
          </span>
          {topCat && (
            <span style={{
              fontSize: 11, color: '#6b7280',
              background: 'var(--surface-subtle)', padding: '2px 7px', borderRadius: 8,
            }}>
              Mostly {CATEGORY_LABELS[topCat[0] as PhoneCategory] || topCat[0]}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>
            🟢 Good rate: <strong style={{ color: 'var(--status-success-fg)' }}>{goodRate}%</strong>
          </span>
          <span style={{
            fontSize: 18, fontWeight: 700, color: 'var(--indigo)', minWidth: 60, textAlign: 'right',
          }}>
            {col.total.toLocaleString()}
          </span>
        </div>
      </div>

      {/* Stacked bar */}
      <MiniBar categories={col.categories} total={col.total} />

      {/* Category chips */}
      <CategoryChips categories={col.categories} total={col.total} />
    </div>
  );
};

// ── SheetAccordion ────────────────────────────────────────────────────────────

const SheetAccordion: React.FC<{ sheet: SheetBreakdown }> = ({ sheet }) => {
  const [open, setOpen] = useState(true);

  // Merge all categories for a sheet-level bar
  const sheetCats: Record<string, number> = {};
  for (const col of sheet.columns) {
    for (const [cat, n] of Object.entries(col.categories)) {
      sheetCats[cat] = (sheetCats[cat] || 0) + n;
    }
  }

  return (
    <div style={{
      border: '1px solid var(--border)', borderRadius: 12,
      background: 'var(--surface)', marginBottom: 16,
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      overflow: 'hidden',
    }}>
      {/* Sheet header — click to expand/collapse */}
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%', padding: '14px 20px',
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between',
          background: open ? 'var(--status-analysis-processing-bg)' : 'var(--surface)',
          border: 'none', borderBottom: open ? '1px solid var(--border)' : 'none',
          cursor: 'pointer', textAlign: 'left', gap: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
          <span style={{ fontSize: 16 }}>📋</span>
          <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-strong)' }}>
            {sheet.sheet_name}
          </span>
          <span style={{
            fontSize: 12, color: 'var(--indigo)',
            background: 'var(--status-analysis-processing-bg)', padding: '2px 8px', borderRadius: 8, fontWeight: 600,
          }}>
            {sheet.total.toLocaleString()} numbers
          </span>
          <span style={{ fontSize: 12, color: 'var(--text-faint)' }}>
            {sheet.columns.length} column{sheet.columns.length !== 1 ? 's' : ''}
          </span>
        </div>
        {/* Sheet-level mini bar */}
        <div style={{ width: 120, flexShrink: 0 }}>
          <MiniBar categories={sheetCats} total={sheet.total} />
        </div>
        <span style={{ fontSize: 12, color: 'var(--text-faint)', flexShrink: 0 }}>
          {open ? '▲ collapse' : '▼ expand'}
        </span>
      </button>

      {open && (
        <div style={{ padding: '14px 20px' }}>
          {sheet.columns.length === 0 ? (
            <p style={{ color: 'var(--text-faint)', fontSize: 13, margin: 0 }}>
              No column data recorded for this sheet.
            </p>
          ) : (
            sheet.columns.map((col) => (
              <ColumnCard key={col.column_name} col={col} />
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const JobDetail: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<JobBreakdown | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!jobId) { navigate('/history'); return; }
    (async () => {
      try {
        const res = await statusApi.getJobBreakdown(jobId);
        setData(res);
      } catch {
        setError('Failed to load job details.');
      } finally {
        setLoading(false);
      }
    })();
  }, [jobId]); // eslint-disable-line

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 60 }}>
      <div className="spinner" />
      <p className="text-muted mt-3">Loading job details…</p>
    </div>
  );

  if (error || !data) return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <div className="alert alert-error" style={{ margin: '24px 0' }}>❌ {error || 'Job not found.'}</div>
      <button className="btn btn-secondary" onClick={() => navigate('/history')}>← Back to History</button>
    </div>
  );

  const st = STATUS_STYLE[data.status] ?? STATUS_STYLE.pending;

  // Global category totals across all sheets
  const globalCats: Record<string, number> = {};
  for (const sheet of data.sheets) {
    for (const col of sheet.columns) {
      for (const [cat, n] of Object.entries(col.categories)) {
        globalCats[cat] = (globalCats[cat] || 0) + n;
      }
    }
  }
  const globalTotal = Object.values(globalCats).reduce((a, b) => a + b, 0);
  const goodRate = globalTotal > 0
    ? (((globalCats['GOOD'] || 0) / globalTotal) * 100).toFixed(1)
    : '0.0';

  return (
    <div style={{ maxWidth: 880, margin: '0 auto', paddingBottom: 48 }}>

      {/* ── Back nav ── */}
      <div style={{ marginBottom: 20 }}>
        <button
          className="btn btn-secondary"
          style={{ fontSize: 13 }}
          onClick={() => navigate('/history')}
        >
          ← Back to History
        </button>
      </div>

      {/* ── Job header card ── */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12,
        padding: '20px 24px', marginBottom: 20,
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'flex-start',
          justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
        }}>
          <div>
            <h2 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 700, color: 'var(--text-strong)' }}>
              {data.filename}
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{
                padding: '3px 10px', borderRadius: 5, fontSize: 11, fontWeight: 700,
                textTransform: 'uppercase', background: st.bg, color: st.color,
              }}>
                {data.status}
              </span>
              <span style={{ fontSize: 13, color: 'var(--text-soft)' }}>
                {data.processed_count.toLocaleString()} / {data.total_numbers.toLocaleString()} numbers
              </span>
              <span style={{ fontSize: 13, color: 'var(--text-soft)' }}>
                ⏱ {elapsed(data.started_at, data.completed_at)}
              </span>
            </div>

            {/* Google-Sheet write-back state. Renders nothing for file uploads. */}
            <div style={{ marginTop: 10 }}>
              <WritebackBadge jobId={data.job_id} />
            </div>

            <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-faint)', display: 'flex', gap: 18, flexWrap: 'wrap' }}>
              <span>Created: {fmtTime(data.created_at)}</span>
              {data.started_at && <span>Started: {fmtTime(data.started_at)}</span>}
              {data.completed_at && <span>Finished: {fmtTime(data.completed_at)}</span>}
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
            {data.export_ready && (
              <button
                onClick={() => downloadApi.triggerDownload(data.job_id, data.filename)}
                style={{
                  display: 'inline-flex', alignItems: 'center', lineHeight: 1,
                  background: 'var(--indigo)', color: '#fff', border: '1px solid var(--indigo)',
                  padding: '9px 18px', borderRadius: 7,
                  cursor: 'pointer', fontSize: 13, fontWeight: 600,
                }}
              >
                ⬇️ Download
              </button>
            )}
            <Link
              to={`/processing/${data.job_id}`}
              style={{
                display: 'inline-flex', alignItems: 'center', lineHeight: 1,
                background: 'var(--surface-subtle)', color: 'var(--text)', border: '1px solid var(--border)',
                padding: '9px 18px', borderRadius: 7,
                textDecoration: 'none', fontSize: 13, fontWeight: 500,
              }}
            >
              Live View
            </Link>
            <RerunFailedAnalysisButton jobId={data.job_id} jobStatus={data.status} />
          </div>
        </div>
      </div>

      {/* ── Global breakdown bar ── */}
      {globalTotal > 0 && (
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12,
          padding: '18px 24px', marginBottom: 20,
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8,
          }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-strong)' }}>
              📊 Overall Results — {globalTotal.toLocaleString()} calls
            </span>
            <span style={{ fontSize: 13, color: 'var(--status-success-fg)', fontWeight: 600 }}>
              🟢 {goodRate}% Human
            </span>
          </div>

          {/* Full-width stacked bar */}
          <div style={{
            display: 'flex', height: 20, borderRadius: 8,
            overflow: 'hidden', marginBottom: 14, background: 'var(--status-neutral-bg)',
          }}>
            {Object.entries(globalCats)
              .sort(([, a], [, b]) => b - a)
              .map(([cat, count]) => (
                <div
                  key={cat}
                  title={`${CATEGORY_LABELS[cat as PhoneCategory] || cat}: ${count} (${((count / globalTotal) * 100).toFixed(1)}%)`}
                  style={{
                    width: `${(count / globalTotal) * 100}%`,
                    background: CATEGORY_COLORS[cat as PhoneCategory] || 'var(--status-neutral-bg)',
                  }}
                />
              ))}
          </div>

          {/* Legend */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {Object.entries(globalCats)
              .sort(([, a], [, b]) => b - a)
              .map(([cat, count]) => (
                <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
                  <div style={{
                    width: 10, height: 10, borderRadius: 2, flexShrink: 0,
                    background: CATEGORY_COLORS[cat as PhoneCategory] || 'var(--status-neutral-bg)',
                  }} />
                  <span style={{ color: 'var(--text)' }}>
                    {CATEGORY_LABELS[cat as PhoneCategory] || cat}:&nbsp;
                    <strong>{count}</strong>
                    <span style={{ color: 'var(--text-faint)', marginLeft: 3 }}>
                      ({((count / globalTotal) * 100).toFixed(1)}%)
                    </span>
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* ── Sheets label ── */}
      <div style={{ marginBottom: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-soft)' }}>
          {data.sheets.length} sheet{data.sheets.length !== 1 ? 's' : ''} processed
        </span>
      </div>

      {/* ── Sheet accordions ── */}
      {data.sheets.length === 0 ? (
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12,
          padding: '32px 24px', textAlign: 'center', color: 'var(--text-faint)', fontSize: 14,
        }}>
          No sheet breakdown available yet. The job may still be processing.
        </div>
      ) : (
        data.sheets.map((sheet) => (
          <SheetAccordion key={sheet.sheet_name} sheet={sheet} />
        ))
      )}
    </div>
  );
};

export default JobDetail;
